import React, {useMemo, useRef, useEffect, useState, useCallback} from "react";
import {
    Box, List, ListSubheader, ListItemButton, Typography, Collapse, Link, Stack, Button, Menu, MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {PHONE_STACK} from "./HeaderBar";
import MiniBars from "./MiniBars";
import {buildAttentionItems} from "./AttentionPanel";
import {usualLine} from "./moved";
import {
    fmtMoneyShort, fmtMoneyShort2, fmtPct0, fmtCompact, fmtDeltaShort, deltaColor, reasonOf, statusMeta, severityOf, EMPTY,
    IDLE_STATUSES, INFO_STATUSES, ALARM_STATUSES, isNilValue, ydDelta, fmtYd, ydColor, fmtSignedMoney, shortProvider, providerCoversNetwork,
    usualLabel, earningMean,
} from "./format";
import {TABULAR, NEUTRAL, PHONE_BAR, SEV, MONO, WARNING_TEXT} from "./tokens";

/** Sort keys. `get(row, ctx)` — `ctx.compareBase` picks the Δd baseline (v2.1). */
export const SORT_KEYS = [
    {key: "rev", label: "Rev", get: (r) => r.rev, fmt: fmtMoneyShort},
    // "Yesterday" view: the big number is yesterday's revenue and the trailing delta is Δ yday (v2.3).
    {key: "rev_last", label: "Yesterday", get: (r) => (isNilValue(r.rev_last) ? null : Number(r.rev_last)), fmt: fmtMoneyShort},
    {key: "rpm_fs", label: "$/1k", get: (r) => r.rpm_fs, fmt: fmtMoneyShort2},
    {key: "delta", label: "Δ wk", get: (r) => (r.rev_prev > 0 ? r.delta_rev_pct : r.rev > 0 ? Number.POSITIVE_INFINITY : null), fmt: null},
    {key: "yest", label: "Δ yday", get: (r, ctx) => (r.yesterday && r.yesterday.state !== "pending" ? ydDelta(r.yesterday, ctx && ctx.compareBase) : null), fmt: null},
    {key: "fs", label: "Sent", get: (r) => r.fs, fmt: null},
    {key: "coverage", label: "Cov", get: (r) => (r.fs > 0 ? r.coverage : null), fmt: fmtPct0},
    {key: "rpm", label: "RPM", get: (r) => r.rpm, fmt: fmtMoneyShort2},
];

const SUB_H = 28;
export const PHONE_TOP = PHONE_STACK;   // section subheaders stick right under the phone chrome (header + tab row)

/** Secondary metrics a row can show on line 2; the sort key is dropped from the list. Revenue is first so it always survives the cut. */
const secondaries = (r, rangeDays, sortKey) => {
    const all = [
        {key: "rev", text: `rev ${fmtMoneyShort(r.rev)}`},
        {key: "rpm_fs", text: `${fmtMoneyShort2(r.rpm_fs)}/1k`},
        {key: "coverage", text: `cov ${r.fs > 0 ? fmtPct0(r.coverage) : EMPTY}`},
        {key: "fs", text: `${fmtCompact(rangeDays ? r.fs / rangeDays : null)}/d`},
        {key: "rpm", text: `rpm ${fmtMoneyShort2(r.rpm)}`},
    ];
    return all.filter((s) => s.key !== sortKey).slice(0, 3).map((s) => s.text).join(" · ");
};

const bigValue = (r, sort, rangeDays, compareBase) => {
    const s = SORT_KEYS.find((k) => k.key === sort.key) || SORT_KEYS[0];
    if (s.key === "delta") return fmtDeltaShort(r.delta_rev_pct, r.rev_prev, r.rev);
    if (s.key === "yest") return fmtYd(r.yesterday, compareBase);
    if (s.key === "fs") return `${fmtCompact(rangeDays ? r.fs / rangeDays : null)}/d`;
    return s.fmt(s.get(r, {compareBase}));
};

/** `partner [· network]` for the phone attention rows. */
const PartnerLine = ({provider, network, prefix}) => (
    <>
        <Box component="span" sx={{color: "text.primary"}}>{shortProvider(provider)}</Box>
        {network && !providerCoversNetwork(provider, network, prefix) && <Box component="span"> · {network}</Box>}
    </>
);

export const Row = React.memo(function Row({r, sort, rangeDays, onSelect, guard, selected, compareBase, child = false}) {
    const sev = severityOf(r.status);
    const showReason = r.status !== "OK";
    const alarm = ALARM_STATUSES.includes(r.status);
    const ydSort = sort.key === "yest";
    const ydayView = sort.key === "rev_last";
    const bigColor = sort.key === "delta" ? deltaColor(r.delta_rev_pct, r.rev_prev, r.rev) : ydSort ? ydColor(r.yesterday, compareBase) : "text.primary";
    return (
        <ListItemButton divider selected={selected} onClick={() => { if (guard()) onSelect(r.tag); }}
                        aria-label={`${r.tag} ${r.site || ""} ${statusMeta(r.status).label}, open details`}
                        sx={{
                            display: "block", minHeight: showReason ? 80 : 64, py: 1, pl: child ? 3 : 1.25, pr: 1.5,
                            borderLeft: "4px solid", borderLeftColor: (t) => (child ? "transparent" : PHONE_BAR[sev](t)),
                            color: sev === "idle" ? NEUTRAL.idleText : "text.primary",
                            "&.Mui-selected": {bgcolor: NEUTRAL.selected},
                        }}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                <Typography component="span" sx={{fontFamily: MONO, fontSize: 15, fontWeight: 700, width: 44, flex: "0 0 auto", color: "inherit"}}>{r.tag}</Typography>
                <Typography component="span" noWrap sx={{fontSize: 13, color: sev === "idle" ? "inherit" : "text.secondary", flex: "1 1 auto", minWidth: 0}}>{r.site || EMPTY}</Typography>
                <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, minWidth: 84, textAlign: "right", flex: "0 0 auto", color: bigColor}}>
                    {bigValue(r, sort, rangeDays, compareBase)}
                </Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                <MiniBars daily={r.daily} width={rangeDays > 14 ? 90 : 61} height={12} gap={rangeDays > 14 ? 1 : 2} partialLast={r.partial_last_day}
                          label={`${r.tag}: daily revenue`}/>
                <Typography component="span" noWrap sx={{fontSize: 12, color: sev === "idle" ? "inherit" : "text.secondary", flex: "1 1 auto", minWidth: 0, ...TABULAR}}>
                    {alarm ? <><PartnerLine provider={r.provider} network={r.type_label} prefix={r.prefix}/>{ydayView && <> · {usualLabel(r.yesterday, earningMean(r))}</>}</> : secondaries(r, rangeDays, sort.key)}
                </Typography>
                {ydayView ? (
                    // Yesterday view: the trailing delta is Δ yday (coloured only when the move is outside the tag's usual range).
                    <Typography component="span" sx={{fontSize: 12, fontWeight: 600, ...TABULAR, flex: "0 0 auto", color: alarm ? "text.secondary" : ydColor(r.yesterday, compareBase)}}
                                title={r.yesterday && r.yesterday.reason ? r.yesterday.reason : undefined}>
                        {fmtYd(r.yesterday, compareBase)}
                    </Typography>
                ) : sort.key !== "delta" && (
                    // Colour budget: while Δd is the sorted (coloured) number, the week Δ turns grey.
                    <Typography component="span" sx={{fontSize: 12, fontWeight: 600, ...TABULAR, flex: "0 0 auto", color: (ydSort || alarm) ? "text.secondary" : deltaColor(r.delta_rev_pct, r.rev_prev, r.rev)}}>
                        {fmtDeltaShort(r.delta_rev_pct, r.rev_prev, r.rev)}
                    </Typography>
                )}
            </Box>
            {showReason && (
                <Typography component="div" sx={{fontSize: 12, mt: 0.5, color: "text.secondary", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.35}} title={reasonOf(r)}>
                    <Box component="span" sx={{fontWeight: 700, color: (t) => SEV[sev].text(t)}}>{statusMeta(r.status).label}</Box> · {reasonOf(r)}
                </Typography>
            )}
        </ListItemButton>
    );
});

/** Grouped attention card: `5 tags` + Σ of the sort metric / summed bars + partner / status · reason; tap expands the tags. */
export const GroupCard = ({g, sort, rangeDays, onSelect, guard, selectedTag, compareBase}) => {
    const [open, setOpen] = useState(false);
    const sev = severityOf(g.status);
    const sum = g.rows.reduce((t, r) => t + (r.rev || 0), 0);
    const big = sort.key === "rev" ? fmtMoneyShort(sum) : sort.key === "rev_last" ? fmtMoneyShort(g.revLast) : `${g.rows.length} tags`;
    return (
        <>
            <ListItemButton divider onClick={() => { if (guard()) setOpen((v) => !v); }} aria-expanded={open}
                            aria-label={`${g.rows.length} tags ${statusMeta(g.status).label}, ${shortProvider(g.provider)}, ${open ? "collapse" : "expand"}`}
                            sx={{display: "block", minHeight: 80, py: 1, pl: 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: (t) => PHONE_BAR[sev](t), bgcolor: "rgba(0,0,0,.015)"}}>
                <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                    <Typography component="span" sx={{fontFamily: MONO, fontSize: 13, fontWeight: 700, flex: "0 0 auto"}}>{g.rows.length} tags</Typography>
                    <Typography component="span" noWrap sx={{color: "text.secondary", flex: "1 1 auto", minWidth: 0, fontFamily: MONO, fontSize: 12}}>
                        {g.rows.slice(0, 5).map((r) => r.tag).join(" ")}{g.rows.length > 5 ? ` +${g.rows.length - 5}` : ""}
                    </Typography>
                    <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, minWidth: 84, textAlign: "right", flex: "0 0 auto", color: "text.primary"}}>{big}</Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                    <MiniBars daily={g.daily} width={rangeDays > 14 ? 90 : 61} height={12} gap={rangeDays > 14 ? 1 : 2} partialLast={g.partialLast} label={`${g.rows.length} tags: summed daily revenue`}/>
                    <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", flex: "1 1 auto", minWidth: 0}}>
                        <PartnerLine provider={g.provider} network={g.network} prefix={g.prefix}/>
                    </Typography>
                    <ExpandMoreIcon sx={{fontSize: 18, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flex: "0 0 auto"}}/>
                </Box>
                <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: "text.secondary"}} title={g.reason}>
                    <Box component="span" sx={{fontWeight: 700, color: (t) => SEV[sev].text(t)}}>{statusMeta(g.status).label}</Box> · {g.reason}
                </Typography>
            </ListItemButton>
            <Collapse in={open} unmountOnExit>
                {g.rows.map((r) => <Row key={r.tag} r={r} sort={sort} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === r.tag} compareBase={compareBase} child/>)}
            </Collapse>
        </>
    );
};

const compare = (sort, ctx) => {
    const s = SORT_KEYS.find((k) => k.key === sort.key) || SORT_KEYS[0];
    const dir = sort.dir === "asc" ? 1 : -1;
    return (a, b) => {
        const va = s.get(a, ctx); const vb = s.get(b, ctx);
        const na = isNilValue(va); const nb = isNilValue(vb);
        if (na && nb) return b.rev - a.rev;
        if (na) return 1;
        if (nb) return -1;
        if (va === vb) return b.rev - a.rev;
        return (va > vb ? 1 : -1) * dir;
    };
};

const Sub = ({children, color, right}) => (
    <ListSubheader disableSticky={false} sx={{
        top: PHONE_STACK, height: SUB_H, lineHeight: `${SUB_H}px`, px: 1.5, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.04em", color: color || "text.secondary", bgcolor: NEUTRAL.headerBg, borderBottom: `1px solid ${NEUTRAL.hairline}`,
        display: "flex", alignItems: "center", justifyContent: "space-between", zIndex: 2, ...TABULAR,
    }}>
        <span>{children}</span>
        {right}
    </ListSubheader>
);

const PHONE_LINE3_MAX = 52;

/** Line 3 of a moved card: driver + short hint (+ `day N` when it still fits 52 chars). */
const movedLine3 = (item) => {
    let line = item.driver || "";
    if (item.hint.short) line += `${line ? " · " : ""}${item.hint.short}`;
    if (item.runDays >= 2) { const s = ` · day ${item.runDays}`; if (line.length + s.length <= PHONE_LINE3_MAX) line += s; }
    return line;
};

/** Signed $ — the card's one coloured element, only when negative. */
const usdColor = (usd) => (usd !== null && usd < 0 ? "error.main" : "text.primary");

/**
 * Moved card (brief §F, 80 px): `tag · site · ±$` / bars · `partner · usually $31/d → $46` / driver · short hint.
 * Neutral grey left border: a mover is not an alarm.
 */
export const MovedCard = ({item, rangeDays, onSelect, guard, selected, compareBase, asOf, child = false}) => {
    const u = usualLine(item.ref, item.rev, compareBase, asOf);
    const line3 = movedLine3(item);
    const showNetwork = item.network && !providerCoversNetwork(item.provider, item.network, item.prefix);
    return (
        <ListItemButton divider selected={selected} onClick={() => { if (guard()) onSelect(item.tag); }}
                        aria-label={`${item.tag} ${item.site || ""} moved ${fmtSignedMoney(item.usd)}, open details`}
                        sx={{display: "block", minHeight: 80, py: 1, pl: child ? 3 : 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: child ? "transparent" : "grey.300",
                            "&.Mui-selected": {bgcolor: NEUTRAL.selected}}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                <Typography component="span" sx={{fontFamily: MONO, fontSize: 15, fontWeight: 700, width: 44, flex: "0 0 auto"}}>{item.tag}</Typography>
                <Typography component="span" noWrap sx={{fontSize: 13, color: "text.secondary", flex: "1 1 auto", minWidth: 0}}>{item.site || EMPTY}</Typography>
                {/* Same right column as the alarm cards: yesterday's $ big, the Δ % under it; the $ delta lives in the text line. */}
                <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, minWidth: 64, textAlign: "right", flex: "0 0 auto", color: "text.primary"}}>
                    {fmtMoneyShort(item.rev)}
                </Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                {!child && <MiniBars daily={item.daily} width={rangeDays > 14 ? 90 : 61} height={12} gap={rangeDays > 14 ? 1 : 2} partialLast={item.partialLast} label={`${item.tag}: daily revenue`}/>}
                <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", flex: "1 1 auto", minWidth: 0, ...TABULAR}}
                            title={`${shortProvider(item.provider)}${showNetwork ? ` · ${item.network}` : ""} · ${u.lead} → ${u.last} (${fmtSignedMoney(item.usd)})`}>
                    <Box component="span" sx={{color: "text.primary"}}>{shortProvider(item.provider)}</Box>
                    {showNetwork && <> · {item.network}</>}
                    {" · "}{u.lead} → <Box component="span" sx={{color: "text.primary"}}>{u.last}</Box> ({fmtSignedMoney(item.usd)})
                </Typography>
                <Typography component="span" sx={{fontSize: 12, fontWeight: 600, ...TABULAR, flex: "0 0 auto", color: usdColor(item.usd)}}>
                    {fmtYd(item.c, compareBase)}
                </Typography>
            </Box>
            {line3 && (
                <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: "text.secondary"}} title={`${item.driver}${item.hint.text ? ` · ${item.hint.text}` : ""}`}>
                    <Box component="span" sx={{color: "text.primary"}}>{item.driver}</Box>{line3.slice((item.driver || "").length)}
                </Typography>
            )}
        </ListItemButton>
    );
};

/** Partner event card: `8 tags · partner` + Σ$ / summed bars + `pays +22% per 1k on all 8` / hint; tap expands its tags. */
export const MovedEventCard = ({item, rangeDays, onSelect, guard, selectedTag, compareBase, asOf}) => {
    const [open, setOpen] = useState(false);
    const u = usualLine(item.ref, item.rev, compareBase, asOf);
    return (
        <>
            <ListItemButton divider onClick={() => { if (guard()) setOpen((v) => !v); }} aria-expanded={open}
                            aria-label={`${item.n} tags of ${shortProvider(item.provider)} moved ${fmtSignedMoney(item.usd)}, ${open ? "collapse" : "expand"}`}
                            sx={{display: "block", minHeight: 80, py: 1, pl: 1.25, pr: 1.5, borderLeft: "4px solid", borderLeftColor: "grey.300", bgcolor: "rgba(0,0,0,.015)"}}>
                <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                    <Typography component="span" sx={{fontFamily: MONO, fontSize: 13, fontWeight: 700, flex: "0 0 auto"}}>{item.n} tags</Typography>
                    <Typography component="span" noWrap sx={{fontSize: 13, color: "text.secondary", flex: "1 1 auto", minWidth: 0}}>· {shortProvider(item.provider)}</Typography>
                    <Typography component="span" sx={{fontSize: 15, fontWeight: 700, ...TABULAR, minWidth: 64, textAlign: "right", flex: "0 0 auto", color: usdColor(item.usd)}}>
                        {fmtSignedMoney(item.usd)}
                    </Typography>
                </Box>
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                    <MiniBars daily={item.daily} width={rangeDays > 14 ? 90 : 61} height={12} gap={rangeDays > 14 ? 1 : 2} partialLast={item.partialLast} label={`${item.n} tags: summed daily revenue`}/>
                    <Typography component="span" noWrap sx={{fontSize: 12, color: "text.primary", flex: "1 1 auto", minWidth: 0, ...TABULAR}} title={item.driver}>{item.driver}</Typography>
                    <ExpandMoreIcon sx={{fontSize: 18, color: "text.secondary", transform: open ? "rotate(180deg)" : "none", transition: "transform .15s", flex: "0 0 auto"}}/>
                </Box>
                <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: "text.secondary", ...TABULAR}} title={`${item.hint.text} · ${u.lead} → ${u.last}`}>
                    {item.hint.text}{item.hint.text ? " · " : ""}{u.lead} → <Box component="span" sx={{color: "text.primary"}}>{u.last}</Box>
                </Typography>
            </ListItemButton>
            <Collapse in={open} unmountOnExit>
                {item.members.map((m) => <MovedCard key={m.tag} item={m} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === m.tag} compareBase={compareBase} asOf={asOf} child/>)}
            </Collapse>
        </>
    );
};

/**
 * Phone list: sticky sort bar, sections (attention → healthy → info → no revenue, expanded by default), ListItemButton rows.
 * `rows` = visible non-idle rows, `idleRows` = NO_REVENUE / NO_TRAFFIC rows (collapsible), `groupBy` = status | group | none.
 */
const TagListMobile = ({rows, idleRows, rangeDays, sort, onSort, onSelect, selectedTag, groupBy, search, filterActive, totalCount, onClear, compareBase}) => {
    const lastScroll = useRef(0);
    const [idleOpen, setIdleOpen] = useState(true);
    useEffect(() => {
        const h = () => { lastScroll.current = Date.now(); };
        window.addEventListener("scroll", h, {capture: true, passive: true});
        return () => window.removeEventListener("scroll", h, {capture: true});
    }, []);
    const guard = useCallback(() => Date.now() - lastScroll.current > 150, []);

    const cmp = useMemo(() => compare(sort, {compareBase}), [sort, compareBase]);
    const sections = useMemo(() => {
        const sorted = [...rows].sort(cmp);
        if (groupBy === "none") return [{key: "all", label: "Tags", rows: sorted}];
        if (groupBy === "group") {
            const by = new Map();
            sorted.forEach((r) => { const k = r.type_label || "Unassigned"; if (!by.has(k)) by.set(k, []); by.get(k).push(r); });
            return Array.from(by.entries())
                .map(([label, rs]) => ({key: label, label, rows: rs, rev: rs.reduce((s, r) => s + (r.rev || 0), 0)}))
                .sort((a, b) => b.rev - a.rev);
        }
        // Money at risk first (like the desktop panel) unless the user picked another sort.
        const defaultSort = sort.key === "rev" && sort.dir === "desc";
        const att = sorted.filter((r) => ALARM_STATUSES.includes(r.status));
        if (defaultSort) att.sort((a, b) => ((b._atRisk || 0) - (a._atRisk || 0)) || (b.rev - a.rev));
        const ok = sorted.filter((r) => r.status === "OK");
        const info = sorted.filter((r) => INFO_STATUSES.includes(r.status));
        // v3: the movers live in the "What happened" view; this list is the plain catalogue.
        return [
            {key: "attention", label: "Needs attention", rows: att, color: WARNING_TEXT, grouped: defaultSort},
            {key: "healthy", label: "Healthy", rows: ok},
            {key: "info", label: "Info", rows: info},
        ].filter((s) => s.rows.length);
    }, [rows, cmp, groupBy, sort]);
    const idleSorted = useMemo(() => [...idleRows].sort(cmp), [idleRows, cmp]);
    const idleAuto = Boolean(search) && idleSorted.length > 0;
    const showIdle = idleOpen || idleAuto;
    const visibleCount = rows.length + idleRows.length;

    const clearRight = filterActive ? (
        <Typography component="span" sx={{fontSize: 11, fontWeight: 600, textTransform: "none", letterSpacing: 0, ...TABULAR}}>
            Showing {visibleCount} of {totalCount} ·{" "}
            <Link component="button" type="button" underline="hover" onClick={onClear} sx={{fontSize: "inherit", fontWeight: 700, verticalAlign: "baseline"}}>Clear</Link>
        </Typography>
    ) : null;

    return (
        <Box>
            {rows.length === 0 && idleRows.length === 0 && (
                <Stack alignItems="center" spacing={1} sx={{py: 6}}>
                    <Typography sx={{fontSize: 14, color: "text.secondary"}}>No tags match these filters</Typography>
                    <Button size="small" variant="outlined" onClick={onClear} sx={{textTransform: "none"}}>Clear filters</Button>
                </Stack>
            )}

            <List disablePadding sx={{"& .MuiListSubheader-root": {position: "sticky"}}}>
                {sections.map((s, i) => (
                    <li key={s.key}>
                        <ul style={{padding: 0, margin: 0, listStyle: "none"}}>
                            <Sub color={s.color} right={i === 0 ? clearRight : null}>{s.label} · {s.rows.length}</Sub>
                            {s.key === "attention" && s.grouped
                                ? buildAttentionItems(s.rows).map((it) => (it.kind === "group"
                                    ? <GroupCard key={it.key} g={it} sort={sort} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selectedTag={selectedTag} compareBase={compareBase}/>
                                    : <Row key={it.key} r={it.row} sort={sort} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === it.row.tag} compareBase={compareBase}/>))
                                : s.rows.map((r) => <Row key={r.tag} r={r} sort={sort} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === r.tag} compareBase={compareBase}/>)}
                        </ul>
                    </li>
                ))}
                {idleSorted.length > 0 && (
                    <li>
                        <ul style={{padding: 0, margin: 0, listStyle: "none"}}>
                            <ListSubheader component="div" onClick={() => setIdleOpen((o) => !o)} role="button" tabIndex={0} aria-expanded={showIdle}
                                           onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setIdleOpen((o) => !o); } }}
                                           sx={{
                                               top: PHONE_STACK, height: SUB_H, lineHeight: `${SUB_H}px`, px: 1.5, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
                                               letterSpacing: "0.04em", color: "text.secondary", bgcolor: NEUTRAL.headerBg, borderBottom: `1px solid ${NEUTRAL.hairline}`,
                                               display: "flex", alignItems: "center", cursor: "pointer", zIndex: 2, ...TABULAR,
                                           }}>
                                Never earned · {idleSorted.length}
                                <ExpandMoreIcon sx={{fontSize: 18, ml: 0.5, transform: showIdle ? "rotate(180deg)" : "none", transition: "transform .15s"}}/>
                                {sections.length === 0 ? <Box sx={{ml: "auto"}}>{clearRight}</Box> : null}
                            </ListSubheader>
                            <Collapse in={showIdle} unmountOnExit>
                                {idleSorted.map((r) => <Row key={r.tag} r={r} sort={sort} rangeDays={rangeDays} onSelect={onSelect} guard={guard} selected={selectedTag === r.tag} compareBase={compareBase}/>)}
                            </Collapse>
                        </ul>
                    </li>
                )}
            </List>
        </Box>
    );
};

/** `$ yday ↓ ▾` — the sort as one 32 px chip with a menu (tab row); tapping the selected key flips the direction. */
export const SortChip = ({sort, onSort}) => {
    const [anchor, setAnchor] = useState(null);
    const cur = SORT_KEYS.find((k) => k.key === sort.key) || SORT_KEYS[0];
    return (
        <>
            <Button size="small" variant="outlined" color="inherit" endIcon={<ArrowDropDownIcon sx={{fontSize: 16}}/>} onClick={(e) => setAnchor(e.currentTarget)}
                    aria-label={`Sort by ${cur.label}, change`} aria-haspopup="menu"
                    sx={{height: 32, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: NEUTRAL.divider, color: "text.primary", px: 1, whiteSpace: "nowrap", ...TABULAR, minWidth: 0}}>
                {cur.label} {sort.dir === "desc" ? "↓" : "↑"}
            </Button>
            <Menu open={Boolean(anchor)} anchorEl={anchor} onClose={() => setAnchor(null)}>
                {SORT_KEYS.map((k) => (
                    <MenuItem key={k.key} selected={k.key === sort.key} sx={{fontSize: 14, minHeight: 40}}
                              onClick={() => { setAnchor(null); onSort(k.key === sort.key ? {key: k.key, dir: sort.dir === "desc" ? "asc" : "desc"} : {key: k.key, dir: "desc"}); }}>
                        {k.label}{k.key === sort.key ? (sort.dir === "desc" ? " ↓ (tap to flip)" : " ↑ (tap to flip)") : ""}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default TagListMobile;
export {IDLE_STATUSES};
